import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/lib/api-client";
import { Building2, Loader2, X } from "lucide-react";
import type { Organization } from "@/api/generated/client/models";

interface SearchResult { id: number; name: string; orgType?: string }

/**
 * CreateOrganizationsPicker — for create dialogs. Manages pending org IDs in memory.
 */
export function CreateOrganizationsPicker({ selectedIds, onChange }: { selectedIds: number[]; onChange: (ids: number[]) => void }) {
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const labelCache = useRef<Map<number, string>>(new Map());

    useEffect(() => {
        if (!search) { setResults([]); setIsSearching(false); return; }
        setIsSearching(true);
        const timer = setTimeout(async () => {
            try {
                const res = await apiClient.organizations.listOrganizations({ search, limit: 15 });
                const orgs = (res.organizations || [])
                    .filter((o: Organization) => !selectedIds.includes(o.id))
                    .map((o: Organization) => ({ id: o.id, name: o.name || `Org #${o.id}`, orgType: o.orgType }));
                setResults(orgs);
            } catch { setResults([]); }
            setIsSearching(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [search, selectedIds]);

    const selectedLabels = selectedIds.map(id => ({ id, label: labelCache.current.get(id) || `Org #${id}` }));

    return (
        <div className="space-y-3">
            <Label className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />Link Organizations</Label>
            {selectedLabels.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selectedLabels.map(({ id, label }) => (
                        <Badge key={id} variant="secondary" className="gap-1 pr-1">
                            {label}
                            <button type="button" aria-label={`Remove ${label}`} onClick={() => onChange(selectedIds.filter(sid => sid !== id))} className="ml-0.5 rounded-full hover:bg-muted p-0.5">
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ))}
                </div>
            )}
            <div>
                <div className="relative">
                    <Input placeholder="Search organizations..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 text-sm pr-8" />
                    {isSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground animate-spin" />}
                </div>
                {!isSearching && search && results.length > 0 && (
                    <div className="border rounded-md max-h-32 overflow-y-auto overflow-x-hidden mt-1">
                        {results.slice(0, 8).map((o) => (
                            <button key={o.id} type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center gap-2 min-w-0" onClick={() => { labelCache.current.set(o.id, o.name); onChange([...selectedIds, o.id]); setSearch(""); }}>
                                <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                <span className="truncate">{o.name}</span>
                                {o.orgType && <span className="text-muted-foreground text-xs ml-auto shrink-0">{o.orgType}</span>}
                            </button>
                        ))}
                    </div>
                )}
                {!isSearching && search && results.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">No matching organizations</p>
                )}
            </div>
        </div>
    );
}
